import { useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import {
  Button,
  Field,
  Modal,
  SelectField,
  StatusBadge,
  Tabs,
  type Column,
  type StatusTone,
} from "@fawkes/ui";

import { formatDateTime } from "../i18n/format";
import { CrematoriumWerklijst } from "./CrematoriumWerklijst";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Crematorium (Epic 21, MR-4 read / MR-W5 write): the Planning overview — the React port of the Jmix
// planning view. Lists the dossiers ready for or scheduled into a cremation run (status
// KLAAR_VOOR_CREMATIE / GEPLAND), soonest planning date first. MR-W5 adds per-row assignment of a
// dossier to a run (CrematieRunService.koppelDossier); ROLE_CREMATORIUM_VIEW reads,
// ROLE_CREMATORIUM_EDIT writes (server-enforced — a 403 surfaces inline).
//
// The url is RELATIVE ("crematorium/planning") — the dataProvider prepends /api once; a leading slash
// would double to /api/api → 404. Search + filters are client-side over the fetched list.

// The processing-form vocabulary reuses the existing crematoriumTarief.vorm.<ID> i18n keys.
const VORMEN = [
  "INDIVIDUEEL",
  "UITGEBREID",
  "COLLECTIEF",
  "RESOMEREN_INDIVIDUEEL",
  "RESOMEREN_COLLECTIEF",
  "BEGRAFENIS_INDIVIDUEEL",
  "BEGRAFENIS_ANONIEM",
  "DESTRUCTIE",
] as const;

// The dossier statuses that make up the planning queue (server-filtered too).
const PLANNING_STATUSES = ["KLAAR_VOOR_CREMATIE", "GEPLAND"] as const;

interface PlanningRow {
  id: string;
  planningDatum: string | null;
  dossierNummer: string | null;
  dierNaam: string | null;
  verwerkingsvorm: string | null;
  status: string | null;
  crematoriumLocatie: string | null;
  runNummer: string | null;
}

interface RunOption {
  id: string;
  runnummer: string | null;
  datumTijd: string | null;
}

function statusTone(status: string | null): StatusTone {
  if (status === "GEPLAND") return "success";
  if (status === "KLAAR_VOOR_CREMATIE") return "warning";
  return "neutral";
}

export function CrematoriumPlanningList() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError, refetch } = useCustom<PlanningRow[]>({
    url: "crematorium/planning",
    method: "get",
  });
  const rows = data?.data ?? [];

  const { data: runsData } = useCustom<RunOption[]>({ url: "crematorium/runs", method: "get" });
  const runs = runsData?.data ?? [];

  const [tab, setTab] = useState<"werklijst" | "runs">("werklijst");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [vormFilter, setVormFilter] = useState("");

  const { mutate, isLoading: saving } = useCustomMutation();
  const [assigning, setAssigning] = useState<PlanningRow | null>(null);
  const [chosenRun, setChosenRun] = useState("");
  const [notice, setNotice] = useState<"forbidden" | "error" | null>(null);

  const openAssign = (r: PlanningRow) => {
    setAssigning(r);
    setChosenRun("");
    setNotice(null);
  };
  const closeAssign = () => {
    setAssigning(null);
    setNotice(null);
  };

  const assign = () => {
    if (!assigning || !chosenRun) return;
    setNotice(null);
    mutate(
      {
        url: `crematorium/runs/${chosenRun}/dossiers`,
        method: "post",
        values: { dossierId: assigning.id },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          closeAssign();
          refetch();
        },
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          setNotice(code === 403 ? "forbidden" : "error");
        },
      },
    );
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (vormFilter && r.verwerkingsvorm !== vormFilter) return false;
      if (q) {
        const hay = `${r.dossierNummer ?? ""} ${r.dierNaam ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, statusFilter, vormFilter]);

  const columns: Column<PlanningRow>[] = [
    {
      header: t("crematoriumPlanning.col.planningDatum"),
      cell: (r) => formatDateTime(r.planningDatum, locale),
    },
    { header: t("crematoriumPlanning.col.dossier"), cell: (r) => r.dossierNummer ?? "—" },
    { header: t("crematoriumPlanning.col.dier"), cell: (r) => r.dierNaam ?? "—" },
    {
      header: t("crematoriumPlanning.col.vorm"),
      cell: (r) => (r.verwerkingsvorm ? t(`crematoriumTarief.vorm.${r.verwerkingsvorm}`) : "—"),
    },
    {
      header: t("crematoriumPlanning.col.status"),
      cell: (r) => (
        <StatusBadge tone={statusTone(r.status)}>
          {r.status ? t(`crematoriumPlanning.status.${r.status}`) : "—"}
        </StatusBadge>
      ),
    },
    { header: t("crematoriumPlanning.col.locatie"), cell: (r) => r.crematoriumLocatie ?? "—" },
    { header: t("crematoriumPlanning.col.run"), cell: (r) => r.runNummer ?? "—" },
    {
      header: t("crematoriumPlanning.col.acties"),
      align: "right",
      cell: (r) => (
        <Button variant="secondary" onClick={() => openAssign(r)}>
          {t("crematoriumPlanning.action.koppel")}
        </Button>
      ),
    },
  ];

  return (
    <div className="product-overview">
      <Tabs
        tabs={[
          { key: "werklijst", label: t("crematoriumPlanning.tab.werklijst") },
          { key: "runs", label: t("crematoriumPlanning.tab.runs") },
        ]}
        activeKey={tab}
        onChange={(key) => setTab(key as "werklijst" | "runs")}
        ariaLabel={t("crematoriumPlanning.tab.groep")}
      />

      {tab === "werklijst" && <CrematoriumWerklijst />}

      {tab === "runs" && (
      <div className="asiel-tarief__panel">
        <div className="asiel-tarief__toolbar">
          <Field
            label={t("crematoriumPlanning.search.label")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <SelectField
            label={t("crematoriumPlanning.filter.status")}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: "", label: t("crematoriumPlanning.filter.all") },
              ...PLANNING_STATUSES.map((s) => ({ value: s, label: t(`crematoriumPlanning.status.${s}`) })),
            ]}
          />
          <SelectField
            label={t("crematoriumPlanning.filter.vorm")}
            value={vormFilter}
            onChange={(e) => setVormFilter(e.target.value)}
            options={[
              { value: "", label: t("crematoriumPlanning.filter.all") },
              ...VORMEN.map((v) => ({ value: v, label: t(`crematoriumTarief.vorm.${v}`) })),
            ]}
          />
          <span className="asiel-tarief__toolbar-spacer" />
          <Button variant="secondary" onClick={() => refetch()}>
            {t("crematoriumPlanning.action.refresh")}
          </Button>
        </div>

        {isError && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t("crematoriumPlanning.load.error")}
          </div>
        )}

        <IndeelbareTabel<PlanningRow> tabelKey="crematorium-planning-list"
          columns={columns}
          rows={filtered}
          getRowKey={(r) => r.id}
          empty={isLoading ? t("common.loading") : t("crematoriumPlanning.empty")}
        />
      </div>
      )}

      <Modal
        open={assigning != null}
        title={t("crematoriumPlanning.assign.title")}
        onClose={closeAssign}
        footer={
          <>
            <Button variant="secondary" disabled={saving} onClick={closeAssign}>
              {t("crematoriumPlanning.assign.annuleren")}
            </Button>
            <Button variant="primary" disabled={saving || !chosenRun} onClick={assign}>
              {t("crematoriumPlanning.assign.opslaan")}
            </Button>
          </>
        }
      >
        <p className="fawkes-muted">
          {t("crematoriumPlanning.assign.dossier", { args: [assigning?.dossierNummer ?? "—"] })}
        </p>
        <SelectField
          label={t("crematoriumPlanning.assign.run")}
          value={chosenRun}
          onChange={(e) => setChosenRun(e.target.value)}
          options={[
            { value: "", label: "—" },
            ...runs.map((r) => ({
              value: r.id,
              label: `${r.runnummer ?? r.id}${r.datumTijd ? ` · ${formatDateTime(r.datumTijd, locale)}` : ""}`,
            })),
          ]}
        />
        {notice && <p className="fawkes-error">{t(`crematoriumPlanning.assign.${notice}`)}</p>}
      </Modal>
    </div>
  );
}
